import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Truck, IndianRupee, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STAFF_ROLES = ["STORE_MANAGER", "CASHIER", "PACKER", "DELIVERY_MANAGER", "DELIVERY_PARTNER"];

// Sprint 31 — additive HR/delivery-partner options
const COMP_TYPES = [
  { value: "salary",       label: "Salary only" },
  { value: "per_delivery", label: "Per Delivery" },
  { value: "hybrid",       label: "Salary + Per Delivery" },
];
const VEHICLE_TYPES = ["bike", "scooter", "ev", "bicycle", "car", "van", "walker"];

const emptyForm = {
  name: "", email: "", phone: "", password: "", role: "CASHIER",
  employee_code: "", department: "", designation: "", salary: "",
  compensation_type: "salary", monthly_salary: "", per_delivery_amount: "",
  vehicle_type: "", vehicle_number: "", delivery_radius_km: "",
  is_active: true,
};

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);   // Sprint 32 — soft-delete confirm

  const fetchRows = async () => {
    setLoading(true);
    try { const { data } = await api.get("/employees?limit=100"); setRows(data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, employee_code: `EMP-${Date.now().toString().slice(-6)}` });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...emptyForm,
      role: r.role || "CASHIER",
      department: r.department || "",
      designation: r.designation || "",
      salary: r.salary ?? "",
      employee_code: r.employee_code || "",
      name: r.name || "", email: r.email || "", phone: r.phone || "", password: "",
      compensation_type:    r.compensation_type    || "salary",
      monthly_salary:       r.monthly_salary       ?? "",
      per_delivery_amount:  r.per_delivery_amount  ?? "",
      vehicle_type:         r.vehicle_type         || "",
      vehicle_number:       r.vehicle_number       || "",
      delivery_radius_km:   r.delivery_radius_km   ?? "",
      is_active:            r.is_active !== false,
    });
    setOpen(true);
  };

  // Field visibility toggles based on compensation type & delivery role.
  const isDelivery  = form.role === "DELIVERY_PARTNER" || form.role === "DELIVERY_MANAGER";
  const showSalary  = form.compensation_type === "salary"       || form.compensation_type === "hybrid";
  const showPerDrop = form.compensation_type === "per_delivery" || form.compensation_type === "hybrid";

  const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

  // Sprint 32 — soft delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/employees/${deleteTarget.id}`);
      toast.success(`Removed ${deleteTarget.name || deleteTarget.employee_code}`);
      setDeleteTarget(null);
      // If the delete happened from inside the edit dialog, close that too.
      if (editing && editing.id === deleteTarget.id) setOpen(false);
      fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const compFields = {
        compensation_type:   form.compensation_type || null,
        monthly_salary:      showSalary  ? numOrNull(form.monthly_salary)      : null,
        per_delivery_amount: showPerDrop ? numOrNull(form.per_delivery_amount) : null,
        vehicle_type:        isDelivery && form.vehicle_type ? form.vehicle_type : null,
        vehicle_number:      isDelivery && form.vehicle_number ? form.vehicle_number : null,
        delivery_radius_km:  isDelivery ? numOrNull(form.delivery_radius_km)   : null,
      };
      if (editing) {
        await api.patch(`/employees/${editing.id}`, {
          role: form.role,
          department:   form.department || null,
          designation:  form.designation || null,
          salary:       numOrNull(form.salary),
          is_active:    !!form.is_active,
          status:       form.is_active ? "active" : "inactive",
          ...compFields,
        });
        toast.success("Employee updated");
      } else {
        await api.post("/employees", {
          name: form.name, email: form.email, phone: form.phone || null,
          password: form.password, role: form.role, employee_code: form.employee_code,
          department: form.department || null, designation: form.designation || null,
          salary: numOrNull(form.salary),
          is_active: !!form.is_active,
          ...compFields,
        });
        toast.success("Employee created");
      }
      setOpen(false); fetchRows();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: "name", header: "Employee", render: (r) => (
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center text-xs font-semibold">
          {(r.name || r.email || "U").slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="font-medium">{r.name || "—"}</div>
          <div className="text-xs text-muted-foreground">{r.email}</div>
        </div>
      </div>
    )},
    { key: "employee_code", header: "Code", render: (r) => <span className="font-mono text-xs">{r.employee_code}</span> },
    { key: "role", header: "Role", render: (r) => <Badge variant="secondary">{r.role}</Badge> },
    { key: "department", header: "Dept.", render: (r) => r.department || "—" },
    { key: "vehicle", header: "Vehicle", render: (r) => r.vehicle_type ? (
      <span className="text-xs inline-flex items-center gap-1 text-muted-foreground"><Truck className="w-3 h-3" />{r.vehicle_type}{r.vehicle_number ? ` · ${r.vehicle_number}` : ""}</span>
    ) : <span className="text-xs text-muted-foreground/60">—</span> },
    { key: "comp", header: "Pay", render: (r) => {
      if (!r.compensation_type) return <span className="text-xs text-muted-foreground/60">—</span>;
      const parts = [];
      if (r.monthly_salary) parts.push(`₹${r.monthly_salary}/mo`);
      if (r.per_delivery_amount) parts.push(`₹${r.per_delivery_amount}/drop`);
      return <span className="text-xs inline-flex items-center gap-1"><IndianRupee className="w-3 h-3" />{parts.join(" + ") || r.compensation_type}</span>;
    }},
    { key: "status", header: "Status", render: (r) => (
      <Badge variant={r.is_active ? "default" : "outline"} className={r.is_active ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : ""}>
        {r.is_active ? "Active" : "Inactive"}
      </Badge>
    )},
    { key: "joined_at", header: "Joined", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.joined_at)}</span> },
    { key: "actions", header: "", className: "w-24 text-right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-emp-${r.id}`} title="Edit">
            <Pencil className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`delete-emp-${r.id}`} title="Delete" className="text-destructive hover:text-destructive hover:bg-destructive/10">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ) },
  ];

  return (
    <div className="p-8" data-testid="employees-page">
      <PageHeader title="Employees" subtitle="Staff & store team"
        actions={<Button onClick={openCreate} className="btn-primary-glow" data-testid="add-emp-btn"><Plus className="w-4 h-4 mr-2" />Onboard employee</Button>} />
      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={openEdit} testId="employees-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit employee" : "Onboard employee"}</DialogTitle></DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            {!editing && (
              <>
                <div className="col-span-2"><Label>Full name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" /></div>
                <div className="col-span-2"><Label>Initial password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 8 chars, letters + digits" data-testid="emp-password" /></div>
              </>
            )}

            <div>
              <Label>Employee code *</Label>
              <Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} disabled={!!editing} data-testid="emp-code" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                <SelectContent>{STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            <div><Label>Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>

            {/* ─── Compensation ─────────────────────────────────────── */}
            <div className="col-span-2 mt-2 pt-3 border-t">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Compensation</div>
            </div>
            <div className="col-span-2">
              <Label>Compensation Type</Label>
              <Select value={form.compensation_type} onValueChange={(v) => setForm({ ...form, compensation_type: v })}>
                <SelectTrigger data-testid="emp-comp-type"><SelectValue /></SelectTrigger>
                <SelectContent>{COMP_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {showSalary && (
              <div>
                <Label>Monthly Salary (₹)</Label>
                <Input type="number" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} placeholder="e.g. 15000" data-testid="emp-monthly-salary" />
              </div>
            )}
            {showPerDrop && (
              <div>
                <Label>Per Delivery Amount (₹)</Label>
                <Input type="number" value={form.per_delivery_amount} onChange={(e) => setForm({ ...form, per_delivery_amount: e.target.value })} placeholder="e.g. 25" data-testid="emp-per-delivery" />
              </div>
            )}

            {/* ─── Vehicle & radius (delivery roles) ─────────────────── */}
            {isDelivery && (
              <>
                <div className="col-span-2 mt-2 pt-3 border-t">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Vehicle & area</div>
                </div>
                <div>
                  <Label>Vehicle Type</Label>
                  <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                    <SelectTrigger data-testid="emp-vehicle-type"><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                    <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vehicle Number</Label>
                  <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })} placeholder="e.g. MP 09 XX 1234" data-testid="emp-vehicle-num" />
                </div>
                <div>
                  <Label>Delivery Radius (KM)</Label>
                  <Input type="number" step="0.5" value={form.delivery_radius_km} onChange={(e) => setForm({ ...form, delivery_radius_km: e.target.value })} placeholder="e.g. 5" data-testid="emp-radius" />
                </div>
              </>
            )}

            {/* ─── Legacy salary (retained for backward compat) ──────── */}
            <div className="col-span-2 mt-2 pt-3 border-t">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Meta</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Base salary <span className="text-[10px]">(legacy field)</span></Label>
              <Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} />
            </div>
            <div className="flex items-end gap-3 rounded-lg border px-3 py-2">
              <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="emp-active" />
              <div className="min-w-0 pb-0.5">
                <div className="text-sm font-medium">Status: {form.is_active ? "Active" : "Inactive"}</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Toggle to disable login & assignments</div>
              </div>
            </div>
          </div>

          <DialogFooter>
            {editing && (
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(editing)}
                className="mr-auto text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
                data-testid="delete-emp-dialog-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />Delete employee
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || (!editing && (!form.name || !form.email || !form.password || !form.employee_code))} className="btn-primary-glow" data-testid="save-emp-btn">
              {saving ? "Saving…" : editing ? "Save" : "Onboard"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sprint 32 — Soft delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-emp-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this employee?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{deleteTarget?.name || deleteTarget?.employee_code}</span> will be
              removed from your team. Existing orders they handled stay intact — they just can't sign in or receive new
              assignments. You can always re-onboard them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-emp-confirm-btn">
              Yes, remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
